import qtiConstants from '../qti/Constants';
import { reportError } from './ErrorReporting';

const { autoFeedbackNames, baseTypes, qtiCardinality } = qtiConstants;
const { SINGLE } = qtiCardinality;
const { ANSWERED, CORRECT, PARTIALLY_CORRECT, UNANSWERED, WRONG } = autoFeedbackNames;

const emptyResponses = ['', '<div><p></p></div>'];
const supportedBaseTypes = [baseTypes.DIRECTED_PAIR, baseTypes.FLOAT, baseTypes.IDENTIFIER, baseTypes.STRING];

const evaluator = {
    evaluateQuestionResponses(question, responseIdentifiers, correctResponsesForQuestion) {
        try {
            const { responseModels } = question;

            const allSupported = responseModels.every(responseModel => supportedBaseTypes.includes(responseModel.baseType));
            if (!allSupported) {
                return null;
            }

            const counts = responseModels.reduce((accumulator, responseModel) => {
                const { identifier } = responseModel;
                const responseIdentifier = responseIdentifiers.find(item => item.identifier === identifier);
                const correctResponses = correctResponsesForQuestion[identifier];
                const feedbackName = evaluator.evaluateResponse(responseIdentifier.userResponse, correctResponses, responseModel);
                accumulator[feedbackName]++;
                return accumulator;
            }, { [ANSWERED]: 0, [CORRECT]: 0, [PARTIALLY_CORRECT]: 0, [UNANSWERED]: 0, [WRONG]: 0 });

            if (counts[ANSWERED] > 0) {
                return ANSWERED;
            }
            if (counts[CORRECT] > 0 && counts[CORRECT] === responseModels.length) {
                return CORRECT;
            }
            if (counts[CORRECT] > 0 || counts[PARTIALLY_CORRECT] > 0) {
                return PARTIALLY_CORRECT;
            }
            if (counts[WRONG] > 0) {
                return WRONG;
            }
            return UNANSWERED;
        } catch (error) {
            reportError(error);
            return null;
        }
    },

    evaluateResponse(userResponse, correctResponses, responseModel) {
        const { baseType, cardinality } = responseModel;
        if (evaluator.isUnanswered(userResponse, baseType)) {
            return UNANSWERED;
        }

        switch (baseType) {
        case baseTypes.DIRECTED_PAIR:
            return evaluator.evaluateDirectedPairResponses(userResponse, correctResponses);

        case baseTypes.FLOAT:
            return evaluator.evaluateFloatResponse(userResponse, correctResponses);

        case baseTypes.IDENTIFIER:
            return evaluator.evaluateIdentifierResponses(userResponse, correctResponses, cardinality);

        default:
            return ANSWERED;
        }
    },

    evaluateDirectedPairResponses(userResponse, correctResponses) {
        const resultCounts = userResponse.reduce((accumulator, response) => {
            if (correctResponses.includes(response)) {
                accumulator.correct++;
            } else {
                accumulator.wrong++;
            }
            return accumulator;
        }, { correct: 0, wrong: 0 });

        if (userResponse.length === resultCounts.correct && userResponse.length === correctResponses.length) {
            return CORRECT;
        }
        if (resultCounts.correct > 0) {
            return PARTIALLY_CORRECT;
        }

        return WRONG;
    },

    evaluateIdentifierResponses(userResponse, correctResponses, cardinality) {
        if (cardinality === SINGLE) {
            return correctResponses.includes(userResponse[0]) ? CORRECT : WRONG;
        }

        const resultCounts = userResponse.reduce((accumulator, response) => {
            if (correctResponses.includes(response)) {
                accumulator.correct++;
            } else {
                accumulator.wrong++;
            }
            return accumulator;
        }, { correct: 0, wrong: 0 });

        if (userResponse.length === resultCounts.correct && userResponse.length === correctResponses.length) {
            return CORRECT;
        }
        if (resultCounts.correct > 0) {
            return PARTIALLY_CORRECT;
        }

        return WRONG;
    },

    evaluateFloatResponse(userResponse, correctResponses) {
        const response = parseFloat(userResponse[0]);
        if (isNaN(response)) {
            return WRONG;
        }

        const lower = parseFloat(correctResponses[0]);
        const upper = parseFloat(correctResponses[1]);

        return response >= lower && response <= upper ? CORRECT : WRONG;
    },

    isUnanswered(userResponse, baseType) {
        if (userResponse.length === 0) {
            return true;
        }

        return baseType === baseTypes.STRING
            && userResponse.every(response => evaluator.isEmptyStringResponse(response));
    },

    /**
     * @param {string} response
     */
    isEmptyStringResponse(response) {
        return emptyResponses.some(emptyResponse => emptyResponse === response);
    },
};

export default evaluator;
